import { DotLottieWorkerReact } from '@lottiefiles/dotlottie-react'
import cn from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Swiper as SwiperType } from 'swiper'
import SwiperCore from 'swiper'
import 'swiper/css'
import 'swiper/css/effect-coverflow'
import 'swiper/css/virtual'
import { Controller, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide, useSwiperSlide } from 'swiper/react'

import { Text } from '@ds'
import {
  AchievementSlug,
  GalaxyButton,
  getCDNUrl,
  getTextWidthInRem,
  LockIcon,
  MarkIcon,
  SwiperArrows,
  useUnlockAchievement,
  webApp,
} from '@shared'
import { SkinItem, useUserActions } from '@store'

import { useUpdateUserAvatarWithEffectSkin } from '../../../../hooks'
import {
  useAvatars,
  useAvatarsEffects,
  useUserAvatar,
  useUserAvatarEffect,
} from '../../hooks'
import { ProfileAvatarsLineIcon } from '../../icons'
import styles from './ProfileAvatarsList.module.scss'

SwiperCore.use([Controller, Virtual])

const hapticFeedback = webApp?.HapticFeedback
const BORDER_SLIDE_OFFSET = 16
const FONT_CONST = '34px / 37px "Neue World", -apple-system, "SF Pro Text", "SF UI Text", system-ui, "Helvetica Neue", Helvetica, Arial, serif, serif'
const AvatarSlideContent = ({ avatar }: { avatar: SkinItem }) => {
  const { isActive } = useSwiperSlide()

  return (
    <div
      className={cn(styles.avatarSlide, {
        [styles.disabled]: !avatar.isAvailable,
      })}
    >
      <img
        src={avatar.iconUrl}
        alt={avatar.name}
        className={cn(styles.avatarImage, { [styles.active]: isActive })}
      />
    </div>
  )
}

const EffectSlideContent = ({ effect }: { effect: SkinItem }) => {
  const [data, setData] = useState<ArrayBuffer | null>(null)
  const { isActive } = useSwiperSlide()

  useEffect(() => {
    if (!effect.appFiles) return

    const url = getCDNUrl(effect.appFiles)

    fetch(url)
      .then((res) => res.arrayBuffer())
      .then((buffer) => {
        setData(buffer)
      })
      .catch((error) => {
        console.error('Failed to load lottie file:', error)
      })
  }, [effect.appFiles])

  return (
    <div
      className={cn(styles.effectSlide, {
        [styles.disabled]: !effect.isAvailable,
        [styles.active]: isActive,
      })}
    >
      {isActive && data && (
        <DotLottieWorkerReact
          data={data}
          loop
          autoplay
          width="100%"
          height="100%"
          className={styles.effectAnimation}
        />
      )}
    </div>
  )
}

const TextSlideContent = ({ text }: { text: string }) => {
  return (
    <Text type="h1" align="center" className={styles.textSlideTitle}>
      {text}
    </Text>
  )
}

export const ProfileAvatarsList = () => {
  const { avatars } = useAvatars()
  const { avatarsEffects } = useAvatarsEffects()
  const { userAvatar } = useUserAvatar()
  const { userAvatarEffect } = useUserAvatarEffect()
  const { fetchProfile } = useUserActions()
  const [avatarSwiper, setAvatarSwiper] = useState<SwiperType | null>(null)
  const [effectSwiper, setEffectSwiper] = useState<SwiperType | null>(null)
  const [avatarTextSwiper, setAvatarTextSwiper] = useState<SwiperType | null>(null)
  const [effectTextSwiper, setEffectTextSwiper] = useState<SwiperType | null>(
    null
  )
  const [currentAvatar, setCurrentAvatar] = useState<string | undefined>(
    userAvatar?.id
  )
  const [currentEffect, setCurrentEffect] = useState<string | undefined>(
    userAvatarEffect?.id
  )
  const [textContainerWidth, setTextContainerWidth] = useState(0)
  const [effectTextContainerWidth, setEffectTextContainerWidth] = useState(0)
  const { unlockAchievement } = useUnlockAchievement()

  useEffect(() => {
    if (userAvatar && currentAvatar !== userAvatar.id) {
      setCurrentAvatar(userAvatar.id)
    }
  }, [userAvatar])

  useEffect(() => {
    if (userAvatarEffect && currentEffect !== userAvatarEffect.id) {
      setCurrentEffect(userAvatarEffect.id)
    }
  }, [userAvatarEffect])

  const { loading: isUpdating, handleUpdateUserAvatarWithEffectSkin } =
    useUpdateUserAvatarWithEffectSkin({
      onSuccess: () => {
        fetchProfile()
        unlockAchievement(AchievementSlug.FACE_SELECTED)
      },
    })

  const initialAvatar = useMemo(() => {
    if (!userAvatar) {
      const availableIndex = avatars.findIndex((avatar) => avatar.isAvailable)
      return availableIndex === -1 ? 0 : availableIndex
    }

    const index = avatars.findIndex((avatar) => avatar.id === userAvatar.id)
    return index === -1 ? 0 : index
  }, [userAvatar, avatars])

  const initialEffect = useMemo(() => {
    if (!userAvatarEffect) {
      const availableIndex = avatarsEffects.findIndex(
        (effect) => effect.isAvailable
      )
      return availableIndex === -1 ? 0 : availableIndex
    }

    const index = avatarsEffects.findIndex(
      (effect) => effect.id === userAvatarEffect.id
    )
    return index === -1 ? 0 : index
  }, [userAvatarEffect, avatarsEffects])

  const isAvatarAvailable = useMemo(() => {
    return avatars.find((avatar) => avatar.id === currentAvatar)?.isAvailable
  }, [avatars, currentAvatar])

  const isEffectAvailable = useMemo(() => {
    return avatarsEffects.find((effect) => effect.id === currentEffect)
      ?.isAvailable
  }, [avatarsEffects, currentEffect])

  const isEquipped = useMemo(() => {
    return (
      userAvatar?.id === currentAvatar && userAvatarEffect?.id === currentEffect
    )
  }, [userAvatar, userAvatarEffect, currentAvatar, currentEffect])

  const isAvatarEquipped = useMemo(() => {
    return userAvatar?.id === currentAvatar
  }, [userAvatar, currentAvatar])

  const isEffectEquipped = useMemo(() => {
    return userAvatarEffect?.id === currentEffect
  }, [userAvatarEffect, currentEffect])

  const isEquipButtonDisabled = useMemo(() => {
    const canChangeAvatar = isAvatarAvailable && !isAvatarEquipped
    const canChangeEffect = isEffectAvailable && !isEffectEquipped
    return !(canChangeAvatar || canChangeEffect) || isUpdating
  }, [
    isAvatarAvailable,
    isEffectAvailable,
    isUpdating,
    isAvatarEquipped,
    isEffectEquipped,
  ])

  const handleEquip = useCallback(async () => {
    if (isEquipButtonDisabled) return

    const newAvatarId = isAvatarAvailable ? currentAvatar : userAvatar?.id
    const newEffectId = isEffectAvailable ? currentEffect : userAvatarEffect?.id

    if (!newAvatarId && !newEffectId) return

    try {
      await handleUpdateUserAvatarWithEffectSkin(newAvatarId, newEffectId)
    } catch (error) {
      console.error('Failed to equip avatar/effect:', error)
    }
  }, [
    isEquipButtonDisabled,
    isAvatarAvailable,
    isEffectAvailable,
    currentAvatar,
    currentEffect,
    userAvatar,
    userAvatarEffect,
    handleUpdateUserAvatarWithEffectSkin,
  ])

  const renderAvatarIcon = useCallback(() => {
    const offset = isAvatarAvailable ? 20 : 12

    const iconStyle = {
      left: `calc(50% + ${textContainerWidth / 2}rem - ${offset}rem)`,
    }

    if (isAvatarEquipped) {
      return <MarkIcon className={styles.textSlideIcon} style={iconStyle} />
    }

    if (!isAvatarAvailable) {
      return <LockIcon className={styles.textSlideIcon} style={iconStyle} />
    }

    return null
  }, [isAvatarEquipped, textContainerWidth, isAvatarAvailable])

  const renderEffectIcon = useCallback(() => {
    const offset = isEffectAvailable ? 20 : 12

    const iconStyle = {
      left: `calc(50% + ${effectTextContainerWidth / 2}rem - ${offset}rem)`,
    }

    if (isEffectEquipped) {
      return (
        <MarkIcon className={styles.effectTextSlideIcon} style={iconStyle} />
      )
    }

    if (!isEffectAvailable) {
      return (
        <LockIcon className={styles.effectTextSlideIcon} style={iconStyle} />
      )
    }

    return null
  }, [isEffectEquipped, effectTextContainerWidth, isEffectAvailable])

  const handleAvatarSwiperChange = useCallback(
    (swiper: SwiperType) => {
      const activeIndex = swiper.activeIndex
      const selectedAvatar = avatars[activeIndex]?.id
      if (selectedAvatar) setCurrentAvatar(selectedAvatar)
    },
    [avatars, setCurrentAvatar]
  )

  const handleAvatarTextSwiperPrev = useCallback(() => {
    if (!avatarTextSwiper) {
      return
    }

    hapticFeedback?.impactOccurred('medium')
    avatarTextSwiper.slidePrev()
  }, [avatarTextSwiper])

  const handleAvatarTextSwiperNext = useCallback(() => {
    if (!avatarTextSwiper) {
      return
    }

    hapticFeedback?.impactOccurred('medium')
    avatarTextSwiper.slideNext()
  }, [avatarTextSwiper])

  const handleEffectSwiperChange = useCallback(
    (swiper: SwiperType) => {
      const activeIndex = swiper.activeIndex
      const selectedEffect = avatarsEffects[activeIndex]?.id
      if (selectedEffect) setCurrentEffect(selectedEffect)
    },
    [avatarsEffects, setCurrentEffect]
  )

  const handleEffectTextSwiperPrev = useCallback(() => {
    if (!effectTextSwiper) {
      return
    }

    hapticFeedback?.impactOccurred('medium')
    effectTextSwiper.slidePrev()
  }, [effectTextSwiper])

  const handleEffectTextSwiperNext = useCallback(() => {
    if (!effectTextSwiper) {
      return
    }

    hapticFeedback?.impactOccurred('medium')
    effectTextSwiper.slideNext()
  }, [effectTextSwiper])

  if (!avatars.length || !avatarsEffects.length) {
    return (
      <div className={styles.container}>
        <div className={styles.backgroundOverlay} />
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <div className={styles.backgroundOverlay} />
      <div className={styles.title}>Choose your avatar</div>
      <GalaxyButton
        withGlow={false}
        className={cn(styles.equipButton, {
          [styles.disabled]: isEquipButtonDisabled,
        })}
        onClick={handleEquip}
        disabled={isEquipButtonDisabled}
      >
        <Text type="body" center className={styles.equipButton__text}>
          {isEquipped ? 'Equipped' : isUpdating ? 'Equipping...' : 'Equip'}
        </Text>
      </GalaxyButton>
      <Swiper
        centeredSlides
        initialSlide={initialAvatar}
        onSwiper={setAvatarSwiper}
        slidesPerView={1}
        allowTouchMove={false}
        watchSlidesProgress
        noSwiping
        noSwipingClass="swiper-no-swiping"
        className={styles.swiper}
        modules={[Virtual]}
        onSlideChange={handleAvatarSwiperChange}
        virtual={{ addSlidesAfter: 2, addSlidesBefore: 2 }}
      >
        {avatars.map((avatar, index) => (
          <SwiperSlide className={styles.avatarSlideContainer} key={avatar.id} virtualIndex={index}>
            <AvatarSlideContent avatar={avatar} />
          </SwiperSlide>
        ))}
      </Swiper>
      <Swiper
        centeredSlides
        initialSlide={initialEffect}
        onSwiper={setEffectSwiper}
        slidesPerView={1}
        allowTouchMove={false}
        watchSlidesProgress
        noSwiping
        noSwipingClass="swiper-no-swiping"
        className={cn(styles.swiper, styles.effectSwiper)}
        onSlideChange={handleEffectSwiperChange}
        modules={[Virtual]}
        virtual={{ addSlidesAfter: 2, addSlidesBefore: 2 }}
      >
        {avatarsEffects.map((effect, index) => (
          <SwiperSlide className={styles.effectSlideContainer} key={effect.id} virtualIndex={index}>
            <EffectSlideContent effect={effect} />
          </SwiperSlide>
        ))}
      </Swiper>
      <SwiperArrows
        className={styles.avatarArrows}
        onClickNext={handleAvatarTextSwiperNext}
        onClockPrev={handleAvatarTextSwiperPrev}
      />
      <div className={styles.textSliderContainer}>
        <Swiper
          grabCursor
          initialSlide={initialAvatar}
          onSwiper={setAvatarTextSwiper}
          centeredSlides
          modules={[Virtual]}
          slidesPerView={1}
          onTouchStart={() => hapticFeedback?.impactOccurred('medium')}
          onInit={(swiper) => {
            const textWidth = getTextWidthInRem(
              avatars[swiper.activeIndex].name,
              FONT_CONST
            )
            setTextContainerWidth(textWidth + BORDER_SLIDE_OFFSET)
          }}
          onSlideChange={(swiper) => {
            const textWidth = getTextWidthInRem(
              avatars[swiper.activeIndex].name,
              FONT_CONST
            )
            setTextContainerWidth(textWidth + BORDER_SLIDE_OFFSET)
            
          }}
          watchSlidesProgress
          controller={{ control: avatarSwiper }}
          className={styles.textSwiper}
          virtual={{ addSlidesAfter: 2, addSlidesBefore: 2 }}
        >
          {avatars.map((avatar, index) => (
            <SwiperSlide
              className={cn(styles.textSlide, {
                [styles.disabled]: !avatar.isAvailable,
              })}
              key={avatar.id}
              virtualIndex={index}
            >
              <TextSlideContent text={avatar.name} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className={cn(styles.textSlideBorder, {
            [styles.disabled]: !isAvatarAvailable,
          })}
          style={{ width: `${textContainerWidth}rem` }}
        />
      </div>
      {renderAvatarIcon()}
      <SwiperArrows
        className={styles.effectArrows}
        onClickNext={handleEffectTextSwiperNext}
        onClockPrev={handleEffectTextSwiperPrev}
      />
      <div className={styles.effectsTitleContainer}>
        <ProfileAvatarsLineIcon />
        <div className={styles.effectsTitleContainer__text}>Effects</div>
        <ProfileAvatarsLineIcon className={styles.effectTitleLine} />
      </div>
      <div className={styles.effectTextSliderContainer}>
        <Swiper
          grabCursor
          initialSlide={initialEffect}
          onSwiper={setEffectTextSwiper}
          centeredSlides
          modules={[Virtual]}
          slidesPerView={1}
          onTouchStart={() => hapticFeedback?.impactOccurred('medium')}
          onInit={(swiper) => {
            const textWidth = getTextWidthInRem(
              avatarsEffects[swiper.activeIndex].name,
              FONT_CONST
            )
            setEffectTextContainerWidth(textWidth + BORDER_SLIDE_OFFSET)
          }}
          onSlideChange={(swiper) => {
            const textWidth = getTextWidthInRem(
              avatarsEffects[swiper.activeIndex].name,
              FONT_CONST
            )
            setEffectTextContainerWidth(textWidth + BORDER_SLIDE_OFFSET)
          }}
          watchSlidesProgress
          controller={{ control: effectSwiper }}
          className={styles.effectTextSwiper}
          virtual={{ addSlidesAfter: 2, addSlidesBefore: 2 }}
        >
          {avatarsEffects.map((effect, index) => (
            <SwiperSlide
              className={cn(styles.textSlide, {
                [styles.disabled]: !effect.isAvailable,
              })}
              key={effect.id}
              virtualIndex={index}
            >
              <TextSlideContent text={effect.name} />
            </SwiperSlide>
          ))}
        </Swiper>
        <div
          className={cn(styles.textSlideBorder, {
            [styles.disabled]: !isEffectAvailable,
          })}
          style={{ width: `${effectTextContainerWidth}rem` }}
        />
      </div>
      {renderEffectIcon()}
    </div>
  )
}
